import type {
  FsType,
  IxdChunk,
  IxdNoteChecklistFinding,
  IxdNoteChecklistLocation,
} from "@/apis/ixd-editor/types";

/**
 * 지적의 위치 모양.
 * - located: 미리보기에서 목차·표·셀을 가리킨다
 * - whole: 연결/별도 주석 전체에 대한 지적(M8). 이동할 곳이 없다
 * - unresolved: 위치를 가리켜야 하는데 미리보기에서 못 찾았다. locationLabel 만 있다
 */
export type FindingLocationShape = "located" | "whole" | "unresolved";

export const findingLocationShape = (
  finding: IxdNoteChecklistFinding,
): FindingLocationShape => {
  if (finding.locations.length > 0) return "located";
  return finding.locationUnresolved ? "unresolved" : "whole";
};

// cell id 에 `:`·`|` 가 들어가므로 따옴표 속성 선택자로 쓰고, 따옴표·역슬래시만 이스케이프한다.
const quote = (value: string) => `"${value.replace(/["\\]/g, "\\$&")}"`;

/**
 * 위치 → 미리보기 DOM 선택자. 셀이 있으면 셀마다, 없으면 표, 표도 없으면 목차 하나.
 * 셀은 (목차, 표) 안으로 한정한다 — 같은 cell id 가 다른 표에도 있을 수 있다.
 */
export const locationSelectors = (
  locations: IxdNoteChecklistLocation[],
): string[] =>
  locations.flatMap(({ chunkId, dlViewId, cellIds }) => {
    const chunk = `[data-chunk-id=${quote(chunkId)}]`;
    if (dlViewId === null) return [chunk];
    const dlView = `${chunk} [data-dl-id=${quote(dlViewId)}]`;
    if (cellIds.length === 0) return [dlView];
    return cellIds.map((cellId) => `${dlView} [data-cell-id=${quote(cellId)}]`);
  });

/**
 * 미리보기에서 누른 요소가 가리키는 지적 index 들. 표·목차 단위 위치는 그 안 어디를 눌러도 해당한다.
 * `selectorsByFinding` 은 지적마다 `locationSelectors` 결과.
 */
export const findingIndexesAt = (
  target: Element,
  selectorsByFinding: string[][],
): number[] =>
  selectorsByFinding.flatMap((selectors, index) =>
    selectors.length > 0 && target.closest(selectors.join(",")) ? [index] : [],
  );

/** 같은 곳에 지적이 여럿이면 누를 때마다 다음 것으로 넘어간다. 없으면 undefined. */
export const nextFindingIndex = (
  matches: number[],
  current: number | null,
): number | undefined => {
  const position = current === null ? -1 : matches.indexOf(current);
  return matches[(position + 1) % matches.length];
};

const CS_TYPE_BY_FS_TYPE: Record<FsType, IxdNoteChecklistFinding["csType"]> = {
  consolidated: "Consolidated",
  separate: "Separated",
};

/** 보고 있는 유형(연결/별도)의 지적. 구분 미상("")은 어느 쪽인지 모르므로 양쪽에 다 보인다. */
export const findingsForFsType = (
  findings: IxdNoteChecklistFinding[],
  fsType: FsType,
): IxdNoteChecklistFinding[] =>
  findings.filter(
    (f) => f.csType === "" || f.csType === CS_TYPE_BY_FS_TYPE[fsType],
  );

/**
 * 위치 → 사람이 읽는 이름 ("14. 차입금 › 차입금 명세"). 지적 하나가 여러 목차·표에 걸칠 때 어디인지 보여준다.
 * 미리보기에 없는 id 면 id 를 그대로 쓴다.
 */
export const describeLocations = (chunks: IxdChunk[]) => {
  const byId = new Map(chunks.map((chunk) => [chunk.id, chunk]));
  return ({ chunkId, dlViewId, cellIds }: IxdNoteChecklistLocation) => {
    const chunk = byId.get(chunkId);
    const parts = [chunk?.title ?? chunkId];
    if (dlViewId !== null) {
      const dlView = chunk?.dlViews.find((v) => v.id === dlViewId);
      parts.push(dlView?.title || dlViewId);
    }
    const name = parts.join(" › ");
    return cellIds.length > 0 ? `${name} (셀 ${cellIds.length})` : name;
  };
};

export const CS_TYPE_LABEL: Record<IxdNoteChecklistFinding["csType"], string> =
  {
    Consolidated: "연결",
    Separated: "별도",
    "": "구분 미상",
  };
